import {
  analyzeSilhouette,
  bakeStep,
  WALK_POSES,
  type WalkPose,
  type Silhouette,
} from "@greg-chick/shared";
import { FRAME, normalizeFrame } from "./strip";

/**
 * 🎬 **서 있는 그림 한 장 → 걷기 네 칸.**
 *
 * 재는 규칙은 `packages/shared/spriteBake` 에 있고(테스트로 묶여 있다), 여기서는 **픽셀을 옮긴다.**
 *
 * ## 왜 통째로 안 흔드나
 * 예전 방식(`scripts/make-walk-strips.mjs`)은 그림 전체를 기울이고 튕겼다. 그러면 캐릭터가
 * **넘어지는 것처럼** 보인다. 머리를 고정하고 몸통만 눌러야 걷는 것으로 읽힌다.
 *
 * ## 🔴 여기서는 이웃 픽셀을 섞어 쓴다(참고한 방식과 다른 점)
 * `sprite-gen` 은 **정수 행 매핑**만 쓴다 — 픽셀아트라 한 픽셀이라도 섞이면 뭉개지기 때문이다.
 * 우리 그림은 **512px 수채 일러스트**다. 여기서 정수로만 늘리고 줄이면 줄이 두 번 복사되어
 * **계단이 보인다.** 부드럽게 섞는 쪽이 이 그림에는 맞다(2~4% 변형이라 흐려지지 않는다).
 * 빌린 것은 **방법**이지 구현 세부가 아니다.
 */

/** 알파 있는 픽셀만 센다 — 반투명 가장자리를 넣으면 실루엣이 실제보다 넓게 잡힌다. */
const ALPHA_MIN = 24;

/** 줄마다 불투명 픽셀 수 — `analyzeSilhouette` 가 먹는 모양. */
export function rowCoverage(cv: HTMLCanvasElement): number[] {
  const ctx = cv.getContext("2d", { willReadFrequently: true });
  if (!ctx) return [];
  const { data, width, height } = ctx.getImageData(0, 0, cv.width, cv.height);
  const rows: number[] = [];
  for (let y = 0; y < height; y++) {
    let n = 0;
    for (let x = 0; x < width; x++) if (data[(y * width + x) * 4 + 3]! >= ALPHA_MIN) n++;
    rows.push(n);
  }
  return rows;
}

/**
 * 한 칸을 그린다. **다리 먼저, 머리+몸통 나중.**
 *
 * ## 두 조각으로 나눈다
 * · 다리(엉덩이~바닥): 무릎이 굽은 만큼 짧게, 발끝은 앞뒤로 밀어서
 * · 머리+몸통(맨 위~엉덩이): **크기를 안 바꾸고** 굽은 만큼 통째로 내린다
 *
 * 그리는 순서가 다리 먼저인 이유: 엉덩이 언저리에서 둘이 1px 겹치는데, 몸통을 위에
 * 얹어야 허리선이 끊겨 보이지 않는다.
 *
 * ## 🔴 여기서는 이웃 픽셀을 섞어 쓴다(참고한 방식과 다른 점)
 * `sprite-gen` 은 **정수 행 매핑**만 쓴다 — 픽셀아트라 한 픽셀이라도 섞이면 뭉개지기 때문이다.
 * 우리 그림은 **512px 수채 일러스트**다. 여기서 정수로만 늘리고 줄이면 줄이 두 번 복사되어
 * **계단이 보인다.** 부드럽게 섞는 쪽이 이 그림에는 맞다(몇 %짜리 변형이라 흐려지지 않는다).
 * 빌린 것은 **방법**이지 구현 세부가 아니다.
 */
function drawPose(src: HTMLCanvasElement, anat: Silhouette, pose: WalkPose): HTMLCanvasElement {
  const out = document.createElement("canvas");
  out.width = src.width;
  out.height = src.height;
  const ctx = out.getContext("2d");
  if (!ctx) return src;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  const s = bakeStep(anat, pose);
  const w = src.width;
  // 전체 띄우기 — 두 발이 모이는 순간의 튐. 발이 잠깐 바닥에서 떨어지는 건 맞다.
  const up = -s.lift;

  /**
   * 🔴 **기울임은 발을 축으로.** 화면 한가운데를 축으로 돌리면 발이 옆으로 미끄러진다 —
   * 걷는 게 아니라 얼음판에서 도는 것으로 보인다.
   */
  if (s.lean) {
    ctx.translate(w / 2, anat.groundRow);
    ctx.rotate((s.lean * Math.PI) / 180);
    ctx.translate(-w / 2, -anat.groundRow);
  }

  // ① 다리: 엉덩이~바닥을 새 높이로. 아래(바닥선)는 고정이고, 굽은 만큼 옆으로 편다.
  const legFromH = s.legFrom.bottom - s.legFrom.top;
  const legToH = s.legTo.bottom - s.legTo.top;
  if (legFromH > 0 && legToH > 0) {
    const lw = w * s.legScaleX;
    ctx.save();
    /**
     * 🔴 **발끝만 민다.** 엉덩이는 제자리에 두고 아래로 갈수록 비스듬히 밀려야
     * 「다리가 흔들린다」로 보인다. 다리를 통째로 옮기면 몸이 끊어져 보인다.
     *
     * 캔버스의 기울이기(shear)는 `y` 에 비례해 `x` 를 민다. 그래서 **엉덩이 줄을 0** 으로
     * 옮겨 놓고 기울인 뒤 되돌린다 — 안 그러면 그림 맨 위를 기준으로 밀려서 엉덩이도 움직인다.
     */
    if (s.legShear !== 0 && legToH > 0) {
      const k = s.legShear / legToH; // 바닥에서 legShear 만큼 밀리는 기울기
      const pivot = s.legTo.top + up;
      ctx.translate(0, pivot);
      ctx.transform(1, 0, k, 1, 0, 0);
      ctx.translate(0, -pivot);
    }
    ctx.drawImage(src, 0, s.legFrom.top, w, legFromH, (w - lw) / 2, s.legTo.top + up, lw, legToH);
    ctx.restore();
  }
  // ② 머리+몸통: 그대로, 무릎이 굽은 만큼 아래로. **크기를 안 바꾼다**(얼굴이 눌리면 딴사람이 된다).
  const upperH = s.upperFrom.bottom - s.upperFrom.top;
  if (upperH > 0) {
    ctx.drawImage(src, 0, s.upperFrom.top, w, upperH, 0, s.upperFrom.top + s.dy + up, w, upperH);
  }
  return out;
}

/**
 * 걷는 그림 한 장에서 걷기 스트립을 만든다.
 *
 * 실루엣을 못 재면(빈 그림·투명하지 않은 그림) `null` — **아무것도 안 만든다.**
 * 엉뚱한 스트립을 만들어 두면 아이 화면에서 캐릭터가 이상하게 떨린다.
 */
export async function bakeWalkStrip(
  img: HTMLImageElement | HTMLCanvasElement,
  poses: readonly WalkPose[] = WALK_POSES,
): Promise<Blob | null> {
  // 먼저 규격에 맞춘다(알파 트림 → 높이 정규화 → 정사각 하단 정렬) — 스트립과 같은 자로 재야
  // 프레임끼리 크기·발높이가 어긋나지 않는다.
  const base = normalizeFrame(img);
  const anat = analyzeSilhouette(rowCoverage(base));
  if (!anat) return null;
  const frames = poses.map((p) => drawPose(base, anat, p));

  /**
   * 🔴 **여기서 다시 정규화하면 안 된다.**
   *
   * 공용 `assembleStripBlob` 은 프레임마다 `normalizeFrame` 을 한 번 더 건다(알파 트림 →
   * 높이 정규화 → 하단 정렬). 손으로 모은 프레임들에는 그게 맞다 — 크기·발높이를 맞춰 주니까.
   *
   * 그런데 우리 프레임은 **일부러 눌러 놓은 것**이다. 다시 정규화하면 눌린 몸이 원래 높이로
   * 늘어나고 띄운 만큼도 바닥에 붙어서, **네 칸이 전부 똑같아진다** — 움직임이 통째로 사라진다.
   * 그래서 여기서는 이미 규격(FRAME 정사각)인 프레임을 **그대로 이어 붙인다.**
   */
  const strip = document.createElement("canvas");
  strip.width = FRAME * frames.length;
  strip.height = FRAME;
  const ctx = strip.getContext("2d");
  if (!ctx) return null;
  frames.forEach((f, i) => ctx.drawImage(f, i * FRAME, 0));
  return await new Promise<Blob | null>((res) => strip.toBlob(res, "image/webp", 0.92));
}
